import { User } from '@creator/sdk/modules/account/account.model';
import { DomainConfig } from '@creator/sdk/modules/config/config.model';
import { DomainsSymbols } from '@creator/sdk/modules/public-settings/public-settings.model';
import { Token } from '@creator/sdk/modules/token/token.model';
import { Post } from '@creator/sdk/modules/upvote/upvote.model';
import { isObjectEmpty } from '@creator/ui/utils/object-utils';
import store from '@src/store';
import { postCollectionFamily, PostCollectionCompositeKeyParams, setPosts } from '@src/store/posts/posts';
import { jotaiStore } from '@src/store/store';

export interface SSRData {
    i18n: Record<string, string>;
    domainConfig: DomainConfig;
    domainSymbols: DomainsSymbols;
    tokens?: Record<Token['tokenName'], Token>;
    tokensAbout?: Record<Token['tokenName'], string>;
    users?: Record<User['id'], User>;
    partnerPosts?: Record<Post['id'], Post>;
    partnerPostsFalirId?: string;
    posts?: Record<Post['id'], Post>;
    pinnedPosts?: Record<Post['id'], Post>;
    metaParams: Record<string, string>;
    whitelistedUsers?: Record<Token['tokenName'], User['id'][]>;
}

export function processSSRData() {
    const ssrData = window.__SSR_DATA__;
    if (!ssrData) {
        console.warn('SSR Data (__SSR_DATA__) not found on window.');
        return null; // Or handle as appropriate
    }

    const {
        tokens = {},
        tokensAbout,
        users,
        posts = {},
        partnerPosts = {},
        partnerPostsFalirId,
        pinnedPosts = {},
        whitelistedUsers,
        domainSymbols,
    } = ssrData;

    // 1. Hydrate the global post cache 
    const allPostsFromSSR = { ...posts, ...partnerPosts, ...pinnedPosts };
    setPosts(allPostsFromSSR);

    // 2. Hydrate Partner Posts Collection State
    if (!isObjectEmpty(partnerPosts)) {
        const postIds = Object.keys(partnerPosts);

        // Safely get tokenName from the first post
        const firstPartnerPost = partnerPosts[postIds[0]];
        // const lastPost = Object.values(partnerPosts)[Object.values(partnerPosts).length - 1];

        const { tokenName } = firstPartnerPost;
        const params: PostCollectionCompositeKeyParams = {
            type: 'partner',
            tokenName,
            flairId: partnerPostsFalirId
        };

        // Get the specific atom instance from the family
        const partnerCollectionAtom = postCollectionFamily(params);

        // Set the initial state for this collection
        jotaiStore.set(partnerCollectionAtom, {
            ids: postIds,
            // We don't get the actual DocumentSnapshot from SSR, so set lastDoc to null.
            // The first 'loadMore' call will fetch the next page correctly.
            // lastDoc: lastPost.id,
            lastDoc: null,
            hasMore: true,
            status: 'success', // Data is successfully loaded from SSR
            error: null
        });
    }

    // 3. Hydrate Pinned Posts Collection State
    if (!isObjectEmpty(pinnedPosts)) {
        const pinnedPostIds = Object.keys(pinnedPosts);
        // Safely get tokenName from the first post
        const firstPinnedPost = pinnedPosts[pinnedPostIds[0]];

        const { tokenName } = firstPinnedPost;

        // Get the specific atom instance from the family
        const pinnedCollectionAtom = postCollectionFamily({ type: 'pinned', tokenName });

        // Set the initial state for this collection
        jotaiStore.set(pinnedCollectionAtom, {
            ids: pinnedPostIds,
            lastDoc: null, // Cannot get DocumentSnapshot from SSR data
            hasMore: false,
            status: 'success',
            error: null,
        });
    }

    if (tokens && !isObjectEmpty(tokens)) store.getActions().token.setTokens(tokens);
    if (tokensAbout && !isObjectEmpty(tokensAbout)) store.getActions().token.setTokensAbout(tokensAbout);
    if (users && !isObjectEmpty(users)) store.getActions().user.setUsersDict(users);
    if (whitelistedUsers && !isObjectEmpty(whitelistedUsers)) store.getActions().token.setTokenPartners(whitelistedUsers);
    if (domainSymbols) store.getActions().publicSettings.set({ key: 'domainsSymbols', value: domainSymbols });

    return ssrData; // Return the original data if needed elsewhere
}